import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';
import SEO from './SEO';
import logoImg from '../assets/logo_transparent.png';
import officeImg from '../assets/serveses/ChatGPT Image Aug 20, 2026, 12_23_46 PM.png';
import teamImg from '../assets/serveses/ChatGPT Image Aug 20, 2026, 12_52_30 PM.png';

const stats = [
  { value: '50+', label: 'Projects Delivered' },
  { value: '30+', label: 'Enterprise Clients' },
  { value: '100+', label: 'Students Trained' },
  { value: '5+', label: 'Years of Excellence' },
];

const values = [
  {
    icon: '🎯',
    title: 'Precision Engineering',
    desc: 'We approach every line of code and every pixel with uncompromising attention to detail, delivering software that is not just functional, but exceptional.',
    color: '#1A3BCC',
    bg: '#EEF2FF',
  },
  {
    icon: '💡',
    title: 'Creative Innovation',
    desc: 'We marry creativity with technology — pushing beyond conventional solutions to deliver products that surprise, delight, and outperform expectations.',
    color: '#FF7B00',
    bg: '#FFF7ED',
  },
  {
    icon: '🤝',
    title: 'Partnership First',
    desc: 'We treat every client as a long-term partner. Your vision drives our roadmap, and your success is the ultimate metric of ours.',
    color: '#0EA5E9',
    bg: '#F0F9FF',
  },
  {
    icon: '🚀',
    title: 'Speed with Quality',
    desc: 'Agile methodologies and modern DevOps pipelines allow us to ship fast without ever cutting corners on reliability, security, or performance.',
    color: '#8B5CF6',
    bg: '#F5F3FF',
  },
  {
    icon: '📚',
    title: 'Knowledge Sharing',
    desc: 'From campus training to open-source contributions, we actively invest in the tech ecosystem — training the next generation of builders.',
    color: '#10B981',
    bg: '#ECFDF5',
  },
  {
    icon: '🌍',
    title: 'Global Impact',
    desc: 'Headquartered in India, serving clients worldwide. We build with a global perspective while staying rooted in local understanding.',
    color: '#F59E0B',
    bg: '#FFFBEB',
  },
];

const milestones = [
  { year: '2021', title: 'Founded', desc: 'QuBright Technologies LLP was established with a vision to bridge creativity and precision in software development.' },
  { year: '2022', title: 'First 10 Clients', desc: 'Delivered 10 enterprise-grade projects ranging from mobile apps to AI-powered platforms across diverse industries.' },
  { year: '2023', title: 'Campus Expansion', desc: 'Launched the SDP & FDP training programmes, partnering with 15+ colleges across South India.' },
  { year: '2024', title: 'Product Launch', desc: 'Launched flagship products — Connect Students, Pulse CRM, Namma CET, and MindPlan AI — serving thousands of users.' },
  { year: '2025', title: 'Global Reach', desc: 'Expanded client base internationally, delivering AI and cloud solutions to enterprises in UAE and Singapore.' },
  { year: '2026', title: 'Scaling Up', desc: 'Doubled team size, launched QuBot AI and QuTrack, and crossed 100+ trained professionals milestone.' },
];

const team = [
  { name: 'Founder & CEO', role: 'Chief Executive Officer', emoji: '👨‍💼', desc: 'Visionary leader with 10+ years in enterprise software and AI product strategy.' },
  { name: 'CTO', role: 'Chief Technology Officer', emoji: '👨‍💻', desc: 'Full-stack architect and AI specialist driving technical excellence across all products.' },
  { name: 'Head of Design', role: 'UI/UX & Brand', emoji: '🎨', desc: 'Award-winning designer crafting pixel-perfect experiences for web and mobile.' },
  { name: 'Head of Training', role: 'Academia & Partnerships', emoji: '📚', desc: 'Ex-professor bridging industry and campus through structured training programmes.' },
];

function StatCard({ stat, index }) {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });
  return (
    <motion.div
      ref={ref}
      initial={{ y: 30, opacity: 0 }}
      animate={inView ? { y: 0, opacity: 1 } : {}}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      className="text-center"
    >
      <p className="font-syne font-extrabold text-5xl md:text-6xl gradient-text mb-2">{stat.value}</p>
      <p className="font-space text-slate-500 text-sm font-semibold uppercase tracking-wider">{stat.label}</p>
    </motion.div>
  );
}

function ValueCard({ value, index }) {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  return (
    <motion.div
      ref={ref}
      initial={{ y: 40, opacity: 0 }}
      animate={inView ? { y: 0, opacity: 1 } : {}}
      transition={{ duration: 0.6, delay: index * 0.08 }}
      className="bg-white rounded-2xl p-6 border border-slate-100 shadow-card hover:shadow-royal transition-all duration-300 hover:-translate-y-1 group"
    >
      <div
        className="w-14 h-14 rounded-2xl flex items-center justify-center text-2xl mb-4 group-hover:scale-110 transition-transform duration-300"
        style={{ background: value.bg }}
      >
        {value.icon}
      </div>
      <h3 className="font-syne font-bold text-lg text-slate-900 mb-2">{value.title}</h3>
      <p className="font-inter text-slate-500 text-sm leading-relaxed">{value.desc}</p>
    </motion.div>
  );
}

export default function AboutUs() {
  return (
    <div className="min-h-screen bg-brand-light">
      <SEO
        title="About Us"
        description="Learn about QuBright Technologies LLP — our story, mission, values, and the passionate team behind cutting-edge software, AI, and training solutions."
        url="https://qubright.web.app/about"
      />
      <Navbar forceOpaque />

      {/* Hero Section */}
      <section className="relative pt-36 pb-24 overflow-hidden bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900">
        <div className="absolute inset-0 grid-pattern opacity-10 pointer-events-none" />
        <div className="absolute top-0 left-0 w-[600px] h-[600px] rounded-full bg-brand-blue/20 blur-[120px] pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full bg-brand-orange/15 blur-[100px] pointer-events-none" />

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <span className="section-label mb-6 inline-block">About QuBright</span>
            <h1 className="font-syne font-extrabold text-5xl md:text-7xl text-white mb-6 leading-tight">
              We Build What
              <span className="block gradient-text mt-1">Tomorrow Needs</span>
            </h1>
            <p className="font-inter text-blue-200 text-xl leading-relaxed max-w-2xl mx-auto mb-10">
              QuBright Technologies LLP is a next-generation software company at the intersection of
              creativity, precision, and purpose — building products and platforms that matter.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <a href="/#contact">
                <button className="btn-primary">Get in Touch</button>
              </a>
              <Link to="/careers">
                <button className="px-6 py-3 rounded-xl border border-white/20 text-white font-space font-semibold hover:bg-white/10 transition-all duration-200">
                  Join Our Team
                </button>
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Wave divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 60L1440 60L1440 20C1200 60 960 0 720 20C480 40 240 0 0 20L0 60Z" fill="#F8FAFC" />
          </svg>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 bg-brand-light">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-10">
            {stats.map((stat, i) => (
              <StatCard key={i} stat={stat} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ x: -50, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <span className="section-label mb-5 inline-block">Our Story</span>
              <h2 className="font-syne font-extrabold text-4xl md:text-5xl text-slate-900 mb-6 leading-tight">
                Where <span className="gradient-text">Creativity</span> Meets Precision
              </h2>
              <p className="font-inter text-slate-500 text-lg leading-relaxed mb-5">
                QuBright was born out of a simple but powerful conviction — that great software is both an art and a science.
                Founded in 2021, we set out to build a company where creative thinking and engineering rigor live together in harmony.
              </p>
              <p className="font-inter text-slate-500 text-base leading-relaxed mb-5">
                What began as a small team of passionate engineers has grown into a multi-disciplinary technology powerhouse — delivering
                AI platforms, mobile apps, web products, cloud infrastructure, and structured training programmes to clients
                across India and beyond.
              </p>
              <p className="font-inter text-slate-500 text-base leading-relaxed mb-8">
                Today, QuBright serves enterprises, startups, and academic institutions — united by our unwavering commitment
                to quality, speed, and genuine partnership.
              </p>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-2xl bg-white border border-slate-100 shadow-md p-2 flex items-center justify-center">
                  <img src={logoImg} alt="QuBright" className="w-full h-full object-contain" />
                </div>
                <div>
                  <p className="font-syne font-bold text-slate-900">QuBright Technologies LLP</p>
                  <p className="font-space text-brand-orange text-sm italic">"Where Creativity Meets Precision"</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ x: 50, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="absolute -top-4 -left-4 w-full h-full rounded-3xl bg-gradient-to-br from-brand-blue/10 to-brand-orange/5 border border-brand-blue/10" />
              <img
                src={officeImg}
                alt="QuBright office and team"
                className="relative rounded-3xl shadow-royal w-full object-cover h-[450px]"
              />
              <div className="absolute bottom-6 left-6 right-6 bg-white/90 backdrop-blur-sm rounded-2xl p-4 shadow-lg">
                <p className="font-space text-brand-blue font-semibold text-sm">📍 Headquartered in India</p>
                <p className="font-inter text-slate-500 text-xs mt-1">Delivering world-class software solutions globally</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-24 bg-brand-light">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="text-center mb-16"
          >
            <span className="section-label mb-5 inline-block">What Drives Us</span>
            <h2 className="font-syne font-extrabold text-4xl md:text-5xl text-slate-900 mb-5">
              Our Core <span className="gradient-text">Values</span>
            </h2>
            <p className="font-inter text-slate-500 text-lg max-w-xl mx-auto">
              These principles are not on a wall — they're baked into every product, every conversation, and every commit.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {values.map((value, i) => (
              <ValueCard key={i} value={value} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 bg-white overflow-hidden">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="text-center mb-16"
          >
            <span className="section-label mb-5 inline-block">Our Journey</span>
            <h2 className="font-syne font-extrabold text-4xl md:text-5xl text-slate-900 mb-5">
              From Idea to <span className="gradient-text">Impact</span>
            </h2>
          </motion.div>

          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-1/2 -translate-x-px top-0 bottom-0 w-0.5 bg-gradient-to-b from-brand-blue via-brand-accent to-brand-orange hidden md:block" />

            <div className="flex flex-col gap-10">
              {milestones.map((m, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: i % 2 === 0 ? -40 : 40 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.08 }}
                  className={`relative md:grid md:grid-cols-2 gap-10 items-center ${i % 2 !== 0 ? 'md:[direction:rtl]' : ''}`}
                >
                  {/* Card */}
                  <div className={`bg-white rounded-2xl border border-slate-100 shadow-card p-6 hover:shadow-royal transition-all duration-300 ${i % 2 !== 0 ? 'md:[direction:ltr]' : ''}`}>
                    <div className="inline-flex items-center gap-2 mb-3">
                      <span className="px-3 py-1 rounded-full bg-gradient-to-r from-brand-blue to-brand-accent text-white text-xs font-space font-bold">
                        {m.year}
                      </span>
                    </div>
                    <h3 className="font-syne font-bold text-xl text-slate-900 mb-2">{m.title}</h3>
                    <p className="font-inter text-slate-500 text-sm leading-relaxed">{m.desc}</p>
                  </div>

                  {/* Centre dot */}
                  <div className="hidden md:flex absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-brand-blue border-4 border-white shadow-md z-10" />

                  {/* Spacer */}
                  <div />
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-24 bg-brand-light">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="text-center mb-16"
          >
            <span className="section-label mb-5 inline-block">The People</span>
            <h2 className="font-syne font-extrabold text-4xl md:text-5xl text-slate-900 mb-5">
              Meet the <span className="gradient-text">Minds</span> Behind QuBright
            </h2>
            <p className="font-inter text-slate-500 text-lg max-w-xl mx-auto">
              A diverse team of engineers, designers, and educators united by a single purpose — building the future.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {team.map((member, i) => (
              <motion.div
                key={i}
                initial={{ y: 40, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="bg-white rounded-3xl p-6 border border-slate-100 shadow-card text-center hover:shadow-royal transition-all duration-300 hover:-translate-y-1 group"
              >
                <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-brand-blue/10 to-brand-orange/5 flex items-center justify-center text-4xl group-hover:scale-110 transition-transform duration-300">
                  {member.emoji}
                </div>
                <p className="font-syne font-bold text-slate-900 text-base mb-0.5">{member.name}</p>
                <p className="font-space text-brand-blue text-xs font-semibold mb-3 uppercase tracking-wide">{member.role}</p>
                <p className="font-inter text-slate-500 text-xs leading-relaxed">{member.desc}</p>
              </motion.div>
            ))}
          </div>

          {/* Team photo */}
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="relative rounded-3xl overflow-hidden shadow-royal"
          >
            <img src={teamImg} alt="QuBright team" className="w-full h-64 md:h-96 object-cover object-top" />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent flex items-end p-8">
              <div>
                <p className="font-syne font-bold text-white text-2xl mb-1">The QuBright Team</p>
                <p className="font-inter text-blue-200 text-sm">Building the future, one commit at a time.</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-br from-brand-blue via-blue-700 to-indigo-800 relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-10 pointer-events-none" />
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-white/5 blur-3xl pointer-events-none" />
        <div className="max-w-3xl mx-auto px-6 text-center relative z-10">
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <h2 className="font-syne font-extrabold text-4xl md:text-5xl text-white mb-5 leading-tight">
              Ready to Build with Us?
            </h2>
            <p className="font-inter text-blue-200 text-lg mb-10 leading-relaxed">
              Whether you're a startup, an enterprise, or an academic institution — we have the expertise, the tools, and the passion to make your vision real.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <a href="/#contact">
                <button className="px-8 py-4 rounded-xl bg-white text-brand-blue font-space font-bold text-sm hover:bg-blue-50 transition-all duration-200 shadow-lg hover:shadow-xl">
                  Start a Project →
                </button>
              </a>
              <Link to="/careers">
                <button className="px-8 py-4 rounded-xl border border-white/30 text-white font-space font-semibold text-sm hover:bg-white/10 transition-all duration-200">
                  Join Our Team
                </button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
